import { useEffect, useState } from 'react';
import api from '../api/Base';
import Status from '../types/Status';
import HostTarget from '../types/host/HostTarget';
import { TARGET_TYPE_HOST } from '../component/Constants';
import { showError } from '../utils/showAlert';

/**
 * Configuration constants for the host status polling system
 */
const CACHE_DURATION = 4000; // 4 seconds cache duration
const POLLING_INTERVAL = 5000; // 5 seconds polling interval

/**
 * Default initial status for hosts before data is loaded
 */
const initialStatus: Status = {
    type: TARGET_TYPE_HOST,
    sensorstatus: 200,
    message: 'OK',
    reason: '',
    updated: new Date(),
    timedout: false,
    state: 'Started',
    status: 200
};

/**
 * Global cache for all host statuses to minimize API calls across components
 */
interface HostStatusCache {
    data: Map<number, Status>;
    timestamp: number;
    isLoading: boolean;
    subscribers: Set<(statuses: Map<number, Status>) => void>;
}

const globalHostStatusCache: HostStatusCache = {
    data: new Map(),
    timestamp: 0,
    isLoading: false,
    subscribers: new Set()
};

let globalPollingTimer: NodeJS.Timeout | null = null;

/**
 * Fetches all host statuses from the API in a single call
 * @returns Promise that resolves to a Map of host IDs to their status objects
 * @throws Error if the API call fails
 */
async function fetchAllHostStatuses(): Promise<Map<number, Status>> {
    try {
        const response = await api.get('host');
        const statusMap = new Map<number, Status>();
        
        if (response?.data && Array.isArray(response.data)) {
            response.data.forEach((host: HostTarget) => {
                if (host?.id) {
                    statusMap.set(host.id, {
                        type: TARGET_TYPE_HOST,
                        sensorstatus: host.sensorstatus ?? 200,
                        message: host.message ?? 'OK',
                        reason: host.reason ?? '',
                        updated: host.updated ?? new Date(),
                        timedout: host.timedout ?? false,
                        state: host.state ?? 'Started',
                        status: host.status ?? 200
                    });
                }
            });
        }
        
        return statusMap;
    } catch (error: any) {
        console.error('Failed to fetch host statuses:', error);
        showError('Failed to fetch host statuses', error.message || error.toString());
        return new Map<number, Status>();
    }
}

/**
 * Polls all host statuses from the API and notifies all subscribers
 * Implements safeguards to prevent concurrent requests
 */
async function pollAllHostStatuses(): Promise<void> {
    if (globalHostStatusCache.isLoading) {
        return; // Prevent concurrent requests
    }

    try {
        globalHostStatusCache.isLoading = true;
        const allStatuses = await fetchAllHostStatuses();
        
        // Update global cache
        globalHostStatusCache.data = allStatuses;
        globalHostStatusCache.timestamp = Date.now();
        
        // Notify all subscribers with the updated data
        globalHostStatusCache.subscribers.forEach(callback => {
            try {
                callback(allStatuses);
            } catch (callbackError) {
                console.error('Error in host status subscriber callback:', callbackError);
            }
        });
    } catch (error) {
        console.error('Failed to fetch host statuses:', error);
    } finally {
        globalHostStatusCache.isLoading = false;
    }
}

/**
 * Starts the global polling timer if not already running
 * Only one polling timer will run regardless of how many components use the hook
 */
function startGlobalPolling(): void {
    if (globalPollingTimer) {
        return; // Already polling
    }

    const poll = (): void => {
        pollAllHostStatuses();
        globalPollingTimer = setTimeout(poll, POLLING_INTERVAL);
    };

    poll(); // Start immediately
}

/**
 * Stops the global polling timer if there are no more subscribers
 * Helps prevent unnecessary API calls when no components need host status data
 */
function stopGlobalPollingIfNoSubscribers(): void {
    if (globalHostStatusCache.subscribers.size === 0 && globalPollingTimer) {
        clearTimeout(globalPollingTimer);
        globalPollingTimer = null;
    }
}

/**
 * React hook for fetching and monitoring host status with optimized global polling
 * 
 * Features:
 * - Global cache to prevent duplicate API calls across multiple components
 * - Automatic polling with configurable intervals
 * - Real-time updates for all subscribers when data changes
 * - Cleanup when components unmount to prevent memory leaks
 * 
 * @param hostId - The ID of the host to monitor (must be > 0)
 * @returns Status object for the specified host
 */
export function useHostStatus(hostId: number): Status {
    const [status, setStatus] = useState<Status>(initialStatus);

    useEffect(() => {
        // Skip polling for invalid host IDs
        if (!hostId || hostId <= 0) {
            return;
        }

        // Callback function to update this component's status when global data changes
        const handleGlobalUpdate = (allStatuses: Map<number, Status>): void => {
            const hostStatus = allStatuses.get(hostId);
            if (hostStatus) {
                setStatus(hostStatus);
            }
        };

        // Subscribe to global status updates
        globalHostStatusCache.subscribers.add(handleGlobalUpdate);

        // Use cached data if it's still fresh
        const now = Date.now();
        const isCacheValid = now - globalHostStatusCache.timestamp < CACHE_DURATION;
        if (isCacheValid) {
            const cachedStatus = globalHostStatusCache.data.get(hostId);
            if (cachedStatus) {
                setStatus(cachedStatus);
            }
        }

        // Start global polling (only starts if not already running)
        startGlobalPolling();

        // Cleanup function runs when component unmounts or hostId changes
        return () => {
            globalHostStatusCache.subscribers.delete(handleGlobalUpdate);
            stopGlobalPollingIfNoSubscribers();
        };
    }, [hostId]);

    return status;
}
